<!-- eslint-disable vue/multi-word-component-names -- wrapper Volt-style, nome a una parola come il componente PrimeVue che avvolge -->
<template>
    <Button
        unstyled
        :pt="theme"
        :ptOptions="{
            mergeProps: ptViewMerge
        }"
    >
        <template v-if="icon" #icon="iconProps">
            <component :is="icon" :class="iconProps?.class" class="size-4 shrink-0" aria-hidden="true" />
        </template>
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
    </Button>
</template>

<script setup lang="ts">
// Wrapper nostro, derivato da Volt 4.5.5 Button/SecondaryButton/DangerButton.
// Differenze: `severity` in un solo componente (le view la usano su Button), prop `icon`
// di tipo componente (icone Lucide) e densità 32px. Non passare insieme `icon` e lo slot #icon.
import Button, { type ButtonPassThroughOptions, type ButtonProps } from 'primevue/button';
import { computed, type Component } from 'vue';
import { ptViewMerge } from './utils';

type Severity = 'primary' | 'secondary' | 'danger' | 'warn' | 'contrast';

interface Props extends /* @vue-ignore */ Omit<ButtonProps, 'icon' | 'severity'> {
    severity?: Severity;
    icon?: Component;
}
const props = defineProps<Props>();

const base = `inline-flex cursor-pointer select-none items-center justify-center overflow-hidden relative
    h-8 px-3 gap-1.5 rounded-md text-sm disabled:pointer-events-none disabled:opacity-60 transition-colors duration-200 border
    focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2
    p-vertical:flex-col p-vertical:h-auto p-vertical:py-2 p-fluid:w-full p-fluid:p-icon-only:w-8
    p-icon-only:w-8 p-icon-only:px-0 p-icon-only:gap-0
    p-icon-only:p-rounded:rounded-full
    p-small:h-7 p-small:px-2.5 p-small:text-xs p-small:p-icon-only:w-7
    p-large:h-10 p-large:px-4 p-large:text-base p-large:p-icon-only:w-10
    p-raised:shadow-sm p-rounded:rounded-[2rem]`;

const severities: Record<Severity, string> = {
    primary: `bg-primary enabled:hover:bg-primary-emphasis enabled:active:bg-primary-emphasis-alt text-primary-contrast
        border-primary enabled:hover:border-primary-emphasis enabled:active:border-primary-emphasis-alt
        focus-visible:outline-primary
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-primary-50 enabled:active:p-outlined:bg-primary-100
        p-outlined:border-primary-200 p-outlined:text-primary
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-primary/5 dark:enabled:active:p-outlined:bg-primary/15
        dark:p-outlined:border-primary-700
        p-text:bg-transparent enabled:hover:p-text:bg-primary-50 enabled:active:p-text:bg-primary-100
        p-text:border-transparent p-text:text-primary
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-primary/5 dark:enabled:active:p-text:bg-primary/15`,
    secondary: `bg-surface-100 enabled:hover:bg-surface-200 enabled:active:bg-surface-300
        border-surface-100 enabled:hover:border-surface-200 enabled:active:border-surface-300
        text-surface-700 enabled:hover:text-surface-800 enabled:active:text-surface-900
        dark:bg-surface-800 dark:enabled:hover:bg-surface-700 dark:enabled:active:bg-surface-600
        dark:border-surface-800 dark:enabled:hover:border-surface-700 dark:enabled:active:border-surface-600
        dark:text-surface-200 dark:enabled:hover:text-surface-100 dark:enabled:active:text-surface-0
        focus-visible:outline-surface-600 dark:focus-visible:outline-surface-300
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-surface-50 enabled:active:p-outlined:bg-surface-100
        p-outlined:border-surface-200 p-outlined:text-surface-600
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-white/5 dark:enabled:active:p-outlined:bg-white/15
        dark:p-outlined:border-surface-700 dark:p-outlined:text-surface-300
        p-text:bg-transparent enabled:hover:p-text:bg-surface-100 enabled:active:p-text:bg-surface-200
        p-text:border-transparent p-text:text-surface-600
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-surface-800 dark:enabled:active:p-text:bg-surface-700
        dark:p-text:text-surface-300`,
    danger: `bg-red-600 enabled:hover:bg-red-700 enabled:active:bg-red-800
        border-red-600 enabled:hover:border-red-700 enabled:active:border-red-800 text-white
        dark:bg-red-400 dark:enabled:hover:bg-red-300 dark:enabled:active:bg-red-200
        dark:border-red-400 dark:enabled:hover:border-red-300 dark:enabled:active:border-red-200 dark:text-red-950
        focus-visible:outline-red-600 dark:focus-visible:outline-red-400
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-red-50 enabled:active:p-outlined:bg-red-100
        p-outlined:border-red-200 p-outlined:text-red-600
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-red-400/5 dark:enabled:active:p-outlined:bg-red-400/15
        dark:p-outlined:border-red-700 dark:p-outlined:text-red-400
        p-text:bg-transparent enabled:hover:p-text:bg-red-50 enabled:active:p-text:bg-red-100
        p-text:border-transparent p-text:text-red-600
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-red-400/5 dark:p-text:text-red-400`,
    warn: `bg-amber-500 enabled:hover:bg-amber-600 enabled:active:bg-amber-700
        border-amber-500 enabled:hover:border-amber-600 enabled:active:border-amber-700 text-amber-950
        dark:bg-amber-400 dark:enabled:hover:bg-amber-300 dark:enabled:active:bg-amber-200
        dark:border-amber-400 dark:enabled:hover:border-amber-300 dark:enabled:active:border-amber-200
        focus-visible:outline-amber-500 dark:focus-visible:outline-amber-400
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-amber-50 enabled:active:p-outlined:bg-amber-100
        p-outlined:border-amber-200 p-outlined:text-amber-700
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-amber-400/5 dark:enabled:active:p-outlined:bg-amber-400/15
        dark:p-outlined:border-amber-700 dark:p-outlined:text-amber-400
        p-text:bg-transparent enabled:hover:p-text:bg-amber-50 enabled:active:p-text:bg-amber-100
        p-text:border-transparent p-text:text-amber-700
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-amber-400/5 dark:p-text:text-amber-400`,
    contrast: `bg-surface-950 enabled:hover:bg-surface-900 enabled:active:bg-surface-800
        border-surface-950 enabled:hover:border-surface-900 enabled:active:border-surface-800 text-white
        dark:bg-surface-0 dark:enabled:hover:bg-surface-100 dark:enabled:active:bg-surface-200
        dark:border-surface-0 dark:enabled:hover:border-surface-100 dark:enabled:active:border-surface-200 dark:text-surface-950
        focus-visible:outline-surface-950 dark:focus-visible:outline-surface-0
        p-outlined:bg-transparent enabled:hover:p-outlined:bg-surface-100 p-outlined:border-surface-700 p-outlined:text-surface-950
        dark:p-outlined:bg-transparent dark:enabled:hover:p-outlined:bg-surface-800 dark:p-outlined:border-surface-500 dark:p-outlined:text-surface-0
        p-text:bg-transparent enabled:hover:p-text:bg-surface-100 p-text:border-transparent p-text:text-surface-950
        dark:p-text:bg-transparent dark:enabled:hover:p-text:bg-surface-800 dark:p-text:text-surface-0`
};

const theme = computed<ButtonPassThroughOptions>(() => ({
    root: `${base} ${severities[props.severity ?? 'primary']}`,
    loadingIcon: `animate-spin`,
    icon: `p-right:order-1 p-bottom:order-2`,
    label: `font-medium p-icon-only:invisible p-icon-only:w-0`,
    pcBadge: {
        root: `min-w-4 h-4 leading-4 bg-primary-contrast rounded-full text-primary text-xs font-bold`
    }
}));
</script>
